/**
 * Positionering van de rondleiding: het opmeten van het doelelement en het
 * bepalen van waar de uitleg komt te staan.
 *
 * ─── WAAROM DIT EEN EIGEN HOOK IS ───────────────────────────────────────────
 *
 * Het "verspringen" van de rondleiding had twee oorzaken, en de tweede werd
 * pas zichtbaar toen de eerste weg was:
 *
 *  1. De meting gebeurde na een vaste wachttijd. Was de route-overgang
 *     langzamer, dan werd er gemeten terwijl het element nog bewoog.
 *
 *  2. De uitleg werd al gerenderd terwijl de meting nog liep. Zonder rect viel
 *     de code terug op "midden op het scherm", en zodra de rect binnenkwam
 *     sprong het blok naar zijn echte plek. Elke stap dus: midden → doel. Dat
 *     is de flits die overbleef.
 *
 * De les uit beide: een positie die nog niet vaststaat, mag niet gerenderd
 * worden. Daarom levert deze hook precies één ding op — een `anchor` die
 * `null` is zolang er nog gemeten wordt. De component hoeft dan geen
 * tussenstand te tekenen, en er is geen plek meer waar een voorlopige waarde
 * per ongeluk in beeld komt.
 *
 * Het uitrekenen zat eerder in de render van de component, waar het bij elke
 * render opnieuw gebeurde en `window.innerWidth` las tijdens het tekenen.
 * Hier gebeurt het één keer per meting.
 */

import { useCallback, useEffect, useState } from 'react';

/** Bovengrens: ~1s bij 60fps. Daarna geven we de gecentreerde plaatsing terug
 *  in plaats van door te blijven meten. Een rondleiding die blijft hangen is
 *  erger dan één zonder spotlight. */
const MAX_MEASURE_FRAMES = 60;

/** Hoeveel frames de rect stil moet staan voordat we hem vertrouwen. */
const STABLE_FRAMES = 2;

/** Onder deze breedte staat de uitleg onderaan als vaste balk. */
const MOBILE_BREAKPOINT = 768;
export type TourPlacement = 'anchored-right' | 'anchored-bottom' | 'center';
export interface TourAnchor {
  /** `null` betekent: geen doelelement gevonden — dan alleen de gecentreerde
   *  uitleg, zonder spotlight. */
  rect: DOMRect | null;
  placement: TourPlacement;
  left: number;
  top: number;
}
export interface UseTourAnchorOptions {
  /** `data-tour`-waarde van het doelelement. */
  targetId: string;
  /** Voorkeurspositie uit de stapdefinitie. */
  preferred: 'top' | 'bottom' | 'left' | 'right' | 'center';
  /** Meten pauzeren (rondleiding dicht, navigatie bezig, slotscherm). */
  enabled: boolean;
}
function computePlacement(rect: DOMRect | null, preferred: UseTourAnchorOptions['preferred'], viewportWidth: number, viewportHeight: number, popoverWidth: number, popoverHeight: number): Omit<TourAnchor, 'rect'> {
  if (!rect || viewportWidth < MOBILE_BREAKPOINT) {
    return {
      placement: 'center',
      left: 0,
      top: 0
    };
  }

  // Past de uitleg rechts naast het doel? De marge is de echte breedte plus
  // ademruimte, niet een rond getal — met een vaste 400 viel het blok bij een
  // smal venster half buiten beeld en sprong de plaatsing heen en weer zodra
  // de rect een fractie schoof.
  const gap = 24;
  const fitsRight = viewportWidth - rect.right >= popoverWidth + gap * 2;
  const fitsBelow = viewportHeight - rect.bottom >= popoverHeight + gap * 2;
  if (fitsRight && preferred !== 'bottom') {
    return {
      placement: 'anchored-right',
      left: rect.right + gap,
      // Verticaal uitlijnen op het midden van het doel, en binnen het venster
      // houden. Eerder stond hier `rect.top - 100`: een vaste verschuiving die
      // bij een doel bovenaan het scherm buiten beeld viel.
      top: clamp(rect.top + rect.height / 2 - popoverHeight / 2, gap, Math.max(gap, viewportHeight - popoverHeight - gap))
    };
  }
  if (fitsBelow) {
    return {
      placement: 'anchored-bottom',
      left: clamp(rect.left + rect.width / 2 - popoverWidth / 2, gap, Math.max(gap, viewportWidth - popoverWidth - gap)),
      top: rect.bottom + gap
    };
  }
  return {
    placement: 'center',
    left: 0,
    top: 0
  };
}
function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}
function sameRect(a: DOMRect, b: DOMRect): boolean {
  return Math.abs(a.top - b.top) < 0.5 && Math.abs(a.left - b.left) < 0.5 && Math.abs(a.width - b.width) < 0.5 && Math.abs(a.height - b.height) < 0.5;
}

/**
 * @param popoverWidth  Breedte van het uitlegblok, nodig om te bepalen of het
 *                      ergens past. Wordt door de component doorgegeven zodat
 *                      de hook niets over de opmaak hoeft te weten.
 */
export function useTourAnchor({
  targetId,
  preferred,
  enabled
}: UseTourAnchorOptions, popoverWidth = 340, popoverHeight = 260): TourAnchor | null {
  const [anchor, setAnchor] = useState<TourAnchor | null>(null);
  const resolve = useCallback((rect: DOMRect | null) => {
    setAnchor({
      rect,
      ...computePlacement(rect, preferred, window.innerWidth, window.innerHeight, popoverWidth, popoverHeight)
    });
  }, [preferred, popoverWidth, popoverHeight]);

  // ─── Meten tot de positie stilstaat ──────────────────────────────────────
  useEffect(() => {
    if (!enabled) return;

    // Meteen op `null`: zolang we niet weten waar de nieuwe stap staat, mag de
    // component niets tekenen. Dit is wat de flits wegneemt.
    setAnchor(null);
    let frame = 0;
    let previous: DOMRect | null = null;
    let stable = 0;
    let elapsed = 0;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      elapsed += 1;
      const el = document.querySelector(`[data-tour="${targetId}"]`);
      if (!el) {
        if (elapsed < MAX_MEASURE_FRAMES) {
          frame = requestAnimationFrame(tick);
        } else {
          // Doel bestaat niet op dit scherm: gecentreerde uitleg, geen
          // spotlight. Beter dan een rondleiding die stilvalt.
          resolve(null);
        }
        return;
      }
      const rect = el.getBoundingClientRect();
      stable = previous && sameRect(rect, previous) ? stable + 1 : 0;
      previous = rect;
      if (stable >= STABLE_FRAMES || elapsed >= MAX_MEASURE_FRAMES) {
        resolve(rect);
        return;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
  }, [enabled, targetId, resolve]);

  // ─── Bijhouden zolang de stap open staat ─────────────────────────────────
  //
  // Er hing eerder alleen een `resize`-listener. Maar de meeste verschuivingen
  // veranderen de vensterbreedte niet: scrollen, data die inlaadt en de pagina
  // hoger maakt, een uitklappend paneel. De spotlight bleef dan op de oude
  // plek tot iemand toevallig het venster verkleinde.
  useEffect(() => {
    if (!enabled || anchor === null) return;
    const el = document.querySelector(`[data-tour="${targetId}"]`);
    if (!el) return;
    let frame = 0;
    const sync = () => {
      // Samenvoegen tot één meting per frame: scroll- en resize-events komen
      // in bursts, en per event opnieuw meten laat de spotlight juist trillen.
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => resolve(el.getBoundingClientRect()));
    };
    window.addEventListener('resize', sync);
    // `capture`: scroll-events van een binnenliggende container bubbelen niet
    // naar window, en juist die containers scrollen hier.
    window.addEventListener('scroll', sync, true);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(sync) : null;
    observer?.observe(el);
    observer?.observe(document.body);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', sync);
      window.removeEventListener('scroll', sync, true);
      observer?.disconnect();
    };
    // `anchor === null` is de trigger om te starten; de rect zelf hoort hier
    // niet in de deps, anders herstart de listener bij elke meting.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, targetId, anchor === null, resolve]);
  return anchor;
}